import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import logger from "../lib/logger";
import { OihMark } from "../components/OihMark";
import { HERITAGE_CATEGORIES } from "../lib/fieldSchemas";
import {
  ArrowRight, BookOpen, Users, Sparkles, HelpCircle,
  Mic, Video, Package, Mail, FileText, ScrollText, Image as ImageIcon,
  Palette, Shirt, Award, Coins, ShieldCheck, HandHeart, Library,
} from "lucide-react";

const ICON_MAP = {
  "Audio": Mic, "Video": Video, "Objects": Package, "Letters": Mail,
  "Documents": FileText, "Old or Rare Books": BookOpen,
  "Manuscripts": ScrollText, "Photographs / Images": ImageIcon,
  "Art / Artwork": Palette, "Textiles / Clothing": Shirt,
  "Memorabilia": Award, "Coins": Coins, "Other Heritage Materials": Sparkles,
};

export default function Home() {
  const [flags, setFlags] = useState({ information_gathering_mode: true });
  const [preview, setPreview] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          API.get("/public/settings"),
          API.get("/public/collection", { params: { limit: 4 } }),
        ]);
        setFlags(s.data);
        setPreview(c.data.items || []);
      } catch (e) { logger.error("Home load failed:", e); }
    })();
  }, []);

  const infoMode = flags.information_gathering_mode;
  const primaryCta = infoMode ? "Tell Us About an Item" : "Share Your Heritage";
  const primaryLink = infoMode ? "/participate/info" : "/register";

  return (
    <div className="min-h-screen bg-cream">
      <TopNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="bg-navy-gradient relative">
          <div className="absolute inset-0 opacity-20 pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(212,181,135,.35), transparent 40%), radial-gradient(circle at 85% 70%, rgba(184,135,74,.25), transparent 45%)" }} />
          <div className="max-w-6xl mx-auto px-6 py-24 md:py-32 relative">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs uppercase tracking-[0.15em] text-[var(--oih-gold-soft)] mb-6" data-testid="hero-eyebrow">
                <Sparkles size={12} /> Office of Ismaili Heritage · Canada
              </div>
              <h1 className="font-display text-5xl md:text-7xl leading-[1.05] text-white mb-6" data-testid="hero-title">
                Preserving Our<br />
                <em className="italic text-[var(--oih-gold-soft)]">Heritage</em> Together
              </h1>
              <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl mb-8" data-testid="hero-subtitle">
                Help us document and preserve the stories, objects, photographs, documents, and
                memories that make up our shared heritage — one family, one Jamat, one artifact at a time.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to={primaryLink} className="btn-gold text-base" data-testid="hero-cta-primary">
                  {primaryCta} <ArrowRight size={18} />
                </Link>
                <Link to="/collection" className="btn-outline text-base bg-transparent text-white border-white/40 hover:!bg-white hover:!text-[var(--oih-navy-deep)]" data-testid="hero-cta-secondary">
                  Explore the Collection
                </Link>
              </div>
              {infoMode && (
                <div className="mt-10 max-w-2xl bg-white/10 border border-white/15 backdrop-blur p-5 rounded-lg text-sm text-white/90 leading-relaxed" data-testid="info-gathering-banner">
                  <strong className="text-white">We are currently gathering information</strong> about heritage materials within the community.
                  Submission of information does not guarantee review, digitization, preservation, or inclusion in the final Heritage Collection.
                  A member of the Heritage team may contact you if additional information is required.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="how-it-works">
        <div className="ornament"><OihMark size={28} tone="light" /></div>
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-3">How It Works</div>
          <h2 className="font-display text-4xl md:text-5xl text-navy">Three Simple Steps</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: HandHeart, num: "1", title: "Share", body: "Tell us about an item, story, photograph, document, or artifact held in your family or community." },
            { icon: ShieldCheck, num: "2", title: "Review", body: "The Heritage team may review your submission and contact you for additional information." },
            { icon: Library,    num: "3", title: "Preserve", body: "Selected materials may become part of the Heritage Collection and help preserve our community's history." },
          ].map((s) => (
            <div key={s.num} className="card-heritage hover-lift text-center" data-testid={`step-${s.num}`}>
              <div className="mx-auto w-14 h-14 rounded-full bg-parchment flex items-center justify-center mb-4">
                <s.icon size={26} className="text-navy" />
              </div>
              <div className="text-xs uppercase tracking-widest text-gold mb-1">Step {s.num}</div>
              <h3 className="font-display text-2xl text-navy mb-2">{s.title}</h3>
              <p className="text-ink-soft leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT CAN BE SUBMITTED — 13 categories */}
      <section className="bg-parchment py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-muted-h mb-3">Contributions Welcome</div>
            <h2 className="font-display text-4xl md:text-5xl text-navy mb-3">What Can Be Submitted?</h2>
            <p className="text-ink-soft max-w-2xl mx-auto">Every family holds fragments of a shared story. Here are the kinds of materials we hope to gather.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {HERITAGE_CATEGORIES.map((cat) => {
              const Icon = ICON_MAP[cat] || Sparkles;
              return (
                <Link
                  key={cat}
                  to={primaryLink}
                  className="group bg-paper border border-heritage rounded-lg p-4 hover-lift text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--oih-navy)]"
                  data-testid={`category-${cat}`}
                >
                  <Icon size={26} className="mx-auto text-navy group-hover:text-gold transition-colors mb-2" />
                  <div className="text-sm font-medium text-ink leading-tight">{cat}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* COLLECTION PREVIEW */}
      {preview.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-h mb-2">From the Collection</div>
              <h2 className="font-display text-4xl text-navy">Recently Published</h2>
            </div>
            <Link to="/collection" className="text-navy hover:underline text-sm font-medium">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {preview.map((it) => (
              <Link key={it.id} to={`/collection/${it.id}`} className="card-heritage hover-lift block" data-testid={`preview-item-${it.id}`}>
                <div className="text-[11px] uppercase tracking-widest text-gold mb-2">{it.category || "Heritage"}</div>
                <h3 className="font-display text-xl text-navy mb-2 line-clamp-2">{it.title}</h3>
                <p className="text-sm text-ink-soft line-clamp-3">{it.description}</p>
                <div className="mt-3 text-xs text-muted-h">{[it.location, it.relevant_dates].filter(Boolean).join(" · ")}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-parchment py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-muted-h mb-3">Community Questions</div>
            <h2 className="font-display text-4xl md:text-5xl text-navy">Frequently Asked</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "Do I need to give up ownership of my heirloom?",
                a: "No. The Heritage Project only asks for information and, where possible, photographs. Physical objects remain with their families." },
              { q: "Is my submission guaranteed to be published?",
                a: "Not immediately. Submissions are reviewed by Heritage staff and community validators. Only approved items are published in the public Heritage Collection." },
              { q: "What if I need help submitting an item?",
                a: "Every submission form includes a request-for-help option. A member of the Heritage team will contact you to assist." },
              { q: "How is my personal information kept secure?",
                a: "Personal information is stored securely and only accessible to authorized Heritage staff. It is never published without your consent." },
              { q: "Can I save my progress and come back later?",
                a: "Yes. Community members can save any submission as a draft and return to complete it at any time." },
            ].map((f, i) => (
              <details key={i} className="bg-paper border border-heritage rounded-lg p-5 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-display text-xl text-navy flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 text-ink-soft leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <HelpCircle size={40} className="text-navy mx-auto mb-4" />
        <h2 className="font-display text-4xl md:text-5xl text-navy mb-4">Have a story to share?</h2>
        <p className="text-lg text-ink-soft mb-8 max-w-2xl mx-auto">
          The heart of this collection is the community that builds it. Add your voice.
        </p>
        <Link to={primaryLink} className="btn-primary text-base" data-testid="footer-cta">
          {primaryCta} <ArrowRight size={18} />
        </Link>
      </section>

      <Footer />
    </div>
  );
}
